import {
  useCallback,
  useEffect,
  useState,
  type MutableRefObject,
  type RefObject,
} from 'react'
import { DESCRIPTION_FIELD, MAX_PIPE_LEVEL } from './constants'
import { rowKey } from './tree-data'
import type { HydraulicTreeRow } from './types'

// The strip floats ABOVE the grid rather than inside the description cell.
// In-cell was not workable: a click on the strip would open the pipe-name
// editor, which swaps the cell's contents and detaches the strip before its
// own click can run; cells also clip their overflow and rows are recycled,
// so a control drawn across a row boundary is fighting the grid on three
// fronts at once. As an overlay it is the topmost layer, outside every
// cell, and the grid never sees the event.
const BAND_ABOVE = 20
const BAND_BELOW = 12

export interface AddSubSpot {
  pipeId: number
  top: number
  left: number
}

interface UseAddSubPipeOverlayArgs {
  hostRef: RefObject<HTMLDivElement>
  rowDataRef: MutableRefObject<HydraulicTreeRow[]>
  bulkEditMode: boolean
  isViewOnly: boolean
}

/** Where the "+ Add Sub Pipe" chip should sit, tracked from the pointer. */
const useAddSubPipeOverlay = ({
  hostRef,
  rowDataRef,
  bulkEditMode,
  isViewOnly,
}: UseAddSubPipeOverlayArgs) => {
  const [addSubSpot, setAddSubSpot] = useState<AddSubSpot | null>(null)
  const clearAddSubSpot = useCallback(() => setAddSubSpot(null), [])

  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    if (!bulkEditMode || isViewOnly) {
      setAddSubSpot(null)
      return
    }

    const onMove = (event: MouseEvent) => {
      const hostRect = host.getBoundingClientRect()
      // getBoundingClientRect is in screen space; the overlay is placed in
      // the container's own space. They differ whenever an ancestor is
      // scaled — the page's entry animation does exactly that — so convert.
      const scale = host.offsetWidth ? hostRect.width / host.offsetWidth : 1
      if (!scale) return

      const byId = new Map(
        rowDataRef.current.map((row) => [rowKey(row), row] as const),
      )

      let next: AddSubSpot | null = null
      host.querySelectorAll('[data-row-id]').forEach((rowEl) => {
        if (next) return
        const rect = rowEl.getBoundingClientRect()
        const nearBoundary =
          event.clientY >= rect.bottom - BAND_ABOVE &&
          event.clientY <= rect.bottom + BAND_BELOW
        if (!nearBoundary) return

        const row = byId.get(rowEl.getAttribute('data-row-id') ?? '')
        // Any pipe that can still take a child — a staged draft included, so
        // a sub-pipe can be filled in before its parent has ever been saved.
        // The chip row is not a pipe. It stays available once a draft has
        // been added, so several sub-pipes can be staged one after another.
        if (!row || row.isCreateRow || Number(row.level) >= MAX_PIPE_LEVEL) {
          return
        }

        const cell = rowEl.querySelector(`[data-col-id="${DESCRIPTION_FIELD}"]`)
        if (!cell) return
        const cellRect = cell.getBoundingClientRect()
        next = {
          pipeId: row.id,
          top: (rect.bottom - hostRect.top) / scale,
          left: (cellRect.left + cellRect.width / 2 - hostRect.left) / scale,
        }
      })

      setAddSubSpot((prev) => {
        if (prev === next) return prev
        if (
          prev &&
          next &&
          prev.pipeId === (next as AddSubSpot).pipeId &&
          Math.abs(prev.top - (next as AddSubSpot).top) < 1 &&
          Math.abs(prev.left - (next as AddSubSpot).left) < 1
        ) {
          return prev
        }
        return next
      })
    }

    host.addEventListener('mousemove', onMove)
    host.addEventListener('mouseleave', clearAddSubSpot)
    return () => {
      host.removeEventListener('mousemove', onMove)
      host.removeEventListener('mouseleave', clearAddSubSpot)
    }
  }, [bulkEditMode, isViewOnly, clearAddSubSpot, hostRef, rowDataRef])

  return { addSubSpot, setAddSubSpot }
}

export default useAddSubPipeOverlay
